import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, FileText, Check, Send, Download, Upload } from "lucide-react";
import { api, API, BACKEND, APP_TYPE_LABELS, DOC_TYPE_LABELS, fmtDDMMYYYY } from "../api";

const Header = ({ me, onLogout }) => (
  <header className="bg-slate-900 text-slate-100 sticky top-0 z-30">
    <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
      <div>
        <p className="text-[10px] tracking-[0.3em] uppercase text-amber-300">Broker Portal</p>
        <p className="text-sm">{me?.name}{me?.broker_firm_name ? ` · ${me.broker_firm_name}` : ""}</p>
      </div>
      <button onClick={onLogout} className="text-[11px] font-bold tracking-[0.14em] uppercase hover:text-amber-200 inline-flex items-center gap-1.5"><LogOut size={14} /> Sign out</button>
    </div>
  </header>
);

export default function BrokerPortal({ me, onLogout }) {
  const [cases, setCases] = useState([]);
  const [handovers, setHandovers] = useState([]);
  const [active, setActive] = useState(null);
  const [docs, setDocs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messageBody, setMessageBody] = useState("");
  const [milestone, setMilestone] = useState({ kind: "application_submitted", reference: "", confirmed_at: new Date().toISOString().slice(0, 10) });

  const load = async () => {
    const a = await api.get(`${API}/applications`);
    setCases(a.data);
    if (a.data.length && !active) {
      switchCase(a.data[0]);
    }
  };
  const switchCase = async (c) => {
    setActive(c);
    const [d, m] = await Promise.all([api.get(`${API}/doc-requests?case_id=${c.id}`), api.get(`${API}/messages?case_id=${c.id}`)]);
    setDocs(d.data); setMessages(m.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openFile = async (fid) => {
    try {
      const { data } = await api.post(`${API}/doc-files/${fid}/download`);
      window.open(`${BACKEND}/api/agency/doc-files/${fid}/stream?token=${data.token}`, "_blank");
    } catch (e) { toast.error(e.response?.data?.detail || "Not allowed"); }
  };
  const ackHandover = async (hid) => {
    try { await api.post(`${API}/handovers/${hid}/ack`); toast.success("Receipt acknowledged"); } catch (e) { toast.error("Could not acknowledge"); }
  };
  const sendMsg = async (scope) => {
    if (!messageBody.trim()) return;
    try {
      const { data } = await api.post(`${API}/messages`, { case_id: active.id, scope, body: messageBody });
      toast.success(data.published ? "Message posted" : "Sent — the agency will review before publishing to the customer");
      setMessageBody("");
      const m = await api.get(`${API}/messages?case_id=${active.id}`); setMessages(m.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
  };
  const recordMilestone = async () => {
    try {
      await api.post(`${API}/broker/milestones`, { case_id: active.id, ...milestone, confirmed_at: `${milestone.confirmed_at}T00:00:00Z` });
      toast.success("Milestone recorded"); setMilestone({ ...milestone, reference: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not record"); }
  };

  if (!active) {
    return (
      <div className="min-h-screen bg-slate-100" data-testid="broker-portal">
        <Header me={me} onLogout={onLogout} />
        <main className="max-w-5xl mx-auto px-5 py-10 text-center text-slate-500" data-testid="broker-portal-empty">
          No cases assigned yet. The agency will share handovers here.
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="broker-portal">
      <Header me={me} onLogout={onLogout} />
      <main className="max-w-5xl mx-auto px-5 py-6 space-y-6">
        <div className="flex gap-2 overflow-x-auto">
          {cases.map(c => (
            <button key={c.id} onClick={() => switchCase(c)} className={`px-3 py-1.5 rounded border text-[12px] whitespace-nowrap ${active.id === c.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"}`}>{c.ref} · {APP_TYPE_LABELS[c.type]}</button>
          ))}
        </div>

        <section className="bg-white border border-slate-200 rounded-xl p-5">
          <h1 className="font-serif text-xl text-slate-800">{APP_TYPE_LABELS[active.type]} <span className="text-slate-500 font-sans text-sm">· {active.ref}</span></h1>
          <p className="text-[12px] text-slate-500 mt-1">Only information explicitly shared with you appears here. Agency reviews your customer-facing updates before publishing.</p>
          <button onClick={() => ackHandover(active.id)} className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.14em] uppercase text-emerald-700 hover:underline"><Check size={13} /> Acknowledge receipt</button>
        </section>

        <section className="bg-white border border-slate-200 rounded-xl p-5">
          <h2 className="font-serif text-lg text-slate-800 flex items-center gap-2"><FileText size={16} /> Shared documents</h2>
          <ul className="divide-y divide-slate-100 mt-3">
            {docs.map(d => (
              <li key={d.id} className="py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{DOC_TYPE_LABELS[d.doc_type] || d.doc_type}</p>
                  <p className="text-[11px] text-slate-500">{d.related_ref?.label} · {d.period || "—"}</p>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {d.files.map(fid => <button key={fid} onClick={() => openFile(fid)} data-testid={`broker-file-${fid}`} className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 rounded text-[11px] hover:border-slate-500"><Download size={11} /> file</button>)}
                </div>
              </li>
            ))}
            {docs.length === 0 && <li className="py-4 text-sm text-slate-500 text-center">No shared documents yet.</li>}
          </ul>
        </section>

        <section className="bg-white border border-slate-200 rounded-xl p-5">
          <h2 className="font-serif text-lg text-slate-800">Record a milestone</h2>
          <p className="text-[12px] text-slate-500 mt-1">Lender-side events — recorded with source and date. The agency never auto-infers these.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            <select className="border border-slate-300 rounded px-3 py-2 text-sm" value={milestone.kind} onChange={(e) => setMilestone({ ...milestone, kind: e.target.value })}>
              <option value="application_submitted">Application submitted</option>
              <option value="valuation">Valuation</option>
              <option value="offer_received">Offer received</option>
            </select>
            <input type="text" placeholder="Reference / lender" className="border border-slate-300 rounded px-3 py-2 text-sm" value={milestone.reference} onChange={(e) => setMilestone({ ...milestone, reference: e.target.value })} />
            <input type="date" className="border border-slate-300 rounded px-3 py-2 text-sm" value={milestone.confirmed_at} onChange={(e) => setMilestone({ ...milestone, confirmed_at: e.target.value })} />
          </div>
          <div className="flex justify-end mt-3">
            <button onClick={recordMilestone} className="inline-flex items-center gap-1 px-4 py-2 rounded bg-slate-900 text-white text-[11px] font-bold tracking-[0.14em] uppercase"><Send size={11} /> Record</button>
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-xl p-5">
          <h2 className="font-serif text-lg text-slate-800">Messages</h2>
          <ul className="space-y-2 mt-3 max-h-56 overflow-y-auto">
            {messages.map(m => (
              <li key={m.id} className="border border-slate-200 rounded p-2">
                <p className="text-[11px] text-slate-500">{m.scope}{!m.published && " · pending agency publish"} · {fmtDDMMYYYY(m.created_at)}</p>
                <p className="text-[13px] text-slate-700 whitespace-pre-wrap">{m.body}</p>
              </li>
            ))}
            {messages.length === 0 && <li className="text-sm text-slate-500 text-center py-3">No messages yet.</li>}
          </ul>
          <textarea rows={2} value={messageBody} onChange={(e) => setMessageBody(e.target.value)} placeholder="Write a message…" className="w-full border border-slate-300 rounded px-3 py-2 text-[13px] mt-3" />
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => sendMsg("broker")} className="px-3 py-1.5 rounded border border-slate-300 text-[11px] font-bold tracking-[0.14em] uppercase hover:border-slate-500">Internal update</button>
            <button onClick={() => sendMsg("broker_customer")} data-testid="broker-propose-customer" className="px-3 py-1.5 rounded bg-slate-900 text-white text-[11px] font-bold tracking-[0.14em] uppercase">Propose customer update</button>
          </div>
        </section>
      </main>
    </div>
  );
}
